import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { Aviso } from '@/lib/supabase/tipos-bd'

/**
 * Los avisos de cada quien.
 *
 * La RLS decide qué se ve: los dirigidos a la persona y los dirigidos al rol
 * que tenga puesto. Por eso aquí no se filtra por destinatario — filtrar en el
 * cliente lo que la base ya filtró es duplicar la regla y arriesgarse a que
 * las dos se separen.
 */
export function useAvisos() {
  const consulta = useQuery({
    queryKey: ['avisos'],
    // Medio minuto: son avisos, no un chat.
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<Aviso[]> => {
      const { data, error } = await supabase
        .from('avisos')
        .select('*')
        .order('creado_en', { ascending: false })
        .limit(40)

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as Aviso[]
    },
  })

  const avisos = consulta.data ?? []

  return {
    avisos,
    sinLeer: avisos.filter((aviso) => aviso.leido_en === null).length,
    cargando: consulta.isLoading,
  }
}

export function useMarcarLeido() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async (avisoId: string): Promise<void> => {
      const { error } = await supabase
        .from('avisos')
        .update({ leido_en: new Date().toISOString() })
        .eq('id', avisoId)

      if (error) throw new Error(mensajeDeError(error))
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['avisos'] })
    },
  })
}
